import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLang } from "@/context/LanguageContext";
import { getConsent, setConsent } from "@/lib/consent";

// GDPR cookie banner — nothing third-party loads until "Αποδοχή".
// Re-openable via window event "cfm-open-cookie-banner" (footer link).
export const CookieBanner = () => {
  const { t } = useLang();
  const c = t.cookies;
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!getConsent()) setVisible(true);
    }, 800);
    const reopen = () => setVisible(true);
    window.addEventListener("cfm-open-cookie-banner", reopen);
    return () => { clearTimeout(timer); window.removeEventListener("cfm-open-cookie-banner", reopen); };
  }, []);

  const choose = (v) => { setConsent(v); setVisible(false); };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 120, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 120, opacity: 0 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="fixed bottom-4 left-4 right-4 sm:right-auto sm:max-w-md z-[90] rounded-2xl bg-white border border-brand/15 shadow-[0_20px_60px_rgba(42,37,34,0.25)] p-5"
          data-testid="cookie-banner"
        >
          <div className="flex items-start gap-3">
            <span className="grid place-items-center h-10 w-10 rounded-full bg-brand-soft text-brand shrink-0">
              <Cookie className="h-5 w-5" />
            </span>
            <div className="min-w-0">
              <p className="font-heading text-lg font-bold text-brand-ink">{c.title}</p>
              <p className="mt-1 text-xs text-brand-ink/70 leading-relaxed">{c.body}</p>
              <div className="mt-3 flex gap-2">
                <Button onClick={() => choose("accepted")} className="rounded-full bg-brand hover:bg-brand-hover text-brand-cream font-bold h-9 px-5 text-sm" data-testid="cookie-accept">
                  {c.accept}
                </Button>
                <Button onClick={() => choose("rejected")} variant="outline" className="rounded-full border-brand/25 text-brand-ink font-bold h-9 px-5 text-sm" data-testid="cookie-reject">
                  {c.reject}
                </Button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
